/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  TrendUp, ChartLineUp, Globe, Shield, Cube, Medal,
  ArrowRight, DownloadSimple, Briefcase, Buildings, Users,
  CurrencyCircleDollar, Target, Certificate,
} from "@phosphor-icons/react";
import {
  MarketingNav, MarketingFooter, Eyebrow, FeatureCard, BulletList,
  CtaBanner, BTN_GREEN,
} from "./_shared";

export default function Investors() {
  useEffect(() => {
    document.title = "Investor Relations · Rescigo Health";
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="page-investors">
      <MarketingNav/>

      {/* HERO */}
      <section className="relative pt-36 pb-24 overflow-hidden bg-gradient-to-b from-[#0A2540] to-[#0E3358] text-white">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #2D5A27 0%, transparent 45%), radial-gradient(circle at 80% 70%, white 0%, transparent 40%)" }}/>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <Eyebrow color="#CFE7C7">Investor Relations</Eyebrow>
            <h1 className="font-heading text-5xl md:text-6xl mt-4 leading-[1.05]">
              Invest in the future of <span className="italic text-[#CFE7C7]">waste intelligence</span> & public-health infrastructure.
            </h1>
            <p className="text-lg text-white/75 mt-6 max-w-xl leading-relaxed">
              Rescigo is building the category-defining regulatory SaaS platform
              for a ₹2.1T global waste-compliance market — with a deep moat in
              multi-tier data, certification, and government relationships.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/#contact" data-testid="inv-cta-ir" className={BTN_GREEN}>
                Contact Investor Relations <ArrowRight size={14} weight="bold"/>
              </Link>
              <a href="mailto:ceo@rescigo.in" data-testid="inv-cta-deck"
                 className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-white/30 text-white hover:bg-white/10 text-sm font-medium">
                <DownloadSimple size={13}/> Request investor deck
              </a>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                       transition={{ duration: 0.7 }}
                       className="grid grid-cols-2 gap-3">
            <StatCard v="₹2.1T" label="Global waste-compliance TAM" sub="by 2030"/>
            <StatCard v="38" label="Live jurisdictions" sub="across 4 states"/>
            <StatCard v="4.2x" label="Revenue growth" sub="YoY CAGR"/>
            <StatCard v="94%" label="Net revenue retention" sub="12-month cohort"/>
          </motion.div>
        </div>
      </section>

      {/* MARKET OPPORTUNITY */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <Eyebrow color="#0A2540">Market opportunity</Eyebrow>
            <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">
              Four tailwinds compounding at once.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-12">
            <FeatureCard accent="#0A2540" Icon={Shield} title="Biomedical Waste Market" desc="India's BMW market projected at ₹84Kcr by 2030 — CAGR 8.2%. Every hospital >30 beds is now mandated to use authorized operators."/>
            <FeatureCard accent="#2D5A27" Icon={Globe} title="ESG Disclosure Mandate" desc="BRSR compliance is now mandatory for top 1000 listed Indian companies. GRI 306 adoption accelerating across private markets."/>
            <FeatureCard accent="#0A2540" Icon={Briefcase} title="Government Digitization Push" desc="Swachh Bharat 2.0 + Smart Cities Mission allocating ₹1.4Lcr to waste infrastructure by 2027."/>
            <FeatureCard accent="#2D5A27" Icon={TrendUp} title="SaaS Software Tailwind" desc="Indian B2B SaaS projected to hit $50B by 2030. Vertical compliance SaaS is the fastest-growing segment."/>
          </div>
        </div>
      </section>

      {/* BUSINESS MODEL */}
      <section className="py-24 bg-[#F7F6F2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Eyebrow color="#0A2540">Business model · 5 revenue streams</Eyebrow>
          <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">Diversified, defensible, recurring.</h2>
          <div className="grid md:grid-cols-5 gap-4 mt-12">
            <Stream title="SaaS · Subscription" sub="Enterprise & mid-market" share="42%" color="#0A2540"/>
            <Stream title="Franchise Royalty" sub="Territory licensing" share="21%" color="#2D5A27"/>
            <Stream title="LMS & Certification" sub="Training academy" share="15%" color="#0A2540"/>
            <Stream title="Government Contracts" sub="B2G ARR" share="14%" color="#2D5A27"/>
            <Stream title="Transaction Fees" sub="Payments · invoicing" share="8%" color="#0A2540"/>
          </div>
          <div className="mt-12 bg-white rounded-2xl border border-slate-200 p-8 grid md:grid-cols-3 gap-6">
            <KPICard label="ARR growth" v="4.2x" sub="YoY" up/>
            <KPICard label="Gross margin" v="78%" sub="software mix" up/>
            <KPICard label="CAC payback" v="9 mo" sub="improving" up/>
          </div>
        </div>
      </section>

      {/* DEFENSIBILITY */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <Eyebrow color="#0A2540">Defensibility</Eyebrow>
            <h2 className="font-heading text-4xl md:text-5xl mt-4 text-slate-900">A compliance moat that widens with scale.</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Every jurisdiction onboarded becomes a regulatory knowledge asset.
              Every certified practitioner extends our authority graph. Every
              audit log becomes evidence of enforceable process — a data-network
              effect only Rescigo can build.
            </p>
            <div className="mt-6"><BulletList color="#0A2540" items={[
              "First-mover position with 4 state pollution control boards",
              "1,280+ certified biomedical waste practitioners",
              "14.7M chain-of-custody events — largest verified dataset",
              "2 filed patents on QR-chain traceability protocol",
              "Government-exclusive SLA commitments (multi-year)",
            ]}/></div>
          </div>
          <div className="bg-gradient-to-br from-[#0A2540] to-[#0E3358] text-white rounded-2xl p-10 relative overflow-hidden">
            <Target size={36} weight="duotone" className="opacity-80"/>
            <div className="font-heading text-3xl mt-5 leading-tight">
              "The defense-grade audit trail is what unlocked our IPO readiness."
            </div>
            <div className="mt-5 text-sm text-white/70">
              — CFO, Top-5 Indian hospital chain · Private briefing · 2026
            </div>
          </div>
        </div>
      </section>

      <CtaBanner
        eyebrow="Investor Relations"
        title="Talk to our founding team."
        body="Our investor relations team shares quarterly performance updates, growth plans, and strategic roadmap under NDA."
        primary={{ label: "Contact IR", to: "/#contact", testid: "inv-cta-bottom" }}
        secondary={{ label: "ceo@rescigo.in", to: "mailto:ceo@rescigo.in" }}
      />
      <MarketingFooter/>
    </div>
  );
}

const StatCard = ({ v, label, sub }) => (
  <div className="bg-white/5 ring-1 ring-white/10 rounded-xl p-5 backdrop-blur-sm">
    <div className="font-heading text-3xl md:text-4xl">{v}</div>
    <div className="text-xs text-white/70 mt-1">{label}</div>
    <div className="text-[10px] text-white/50 mt-0.5">{sub}</div>
  </div>
);

const Stream = ({ title, sub, share, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 relative overflow-hidden">
    <div className="absolute top-0 left-0 right-0 h-1" style={{ background: color }}/>
    <div className="font-heading text-xl mt-1 text-slate-900">{share}</div>
    <div className="text-sm font-medium text-slate-800 mt-1">{title}</div>
    <div className="text-[11px] text-slate-500">{sub}</div>
  </div>
);

const KPICard = ({ label, v, sub, up }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
    <div className="font-heading text-4xl mt-1 text-slate-900">{v}</div>
    <div className={`text-xs mt-1 inline-flex items-center gap-1 ${up ? "text-emerald-700" : "text-slate-500"}`}>
      {up && <TrendUp size={11} weight="bold"/>} {sub}
    </div>
  </div>
);
